import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { AccessChips } from "@/payroll/workbook-access/access-chips";
import type {
  AccessArea,
  AccessAssignment,
  AccessKeySetting,
} from "@/payroll/workbook-access/types";

interface LockedAccessSummaryProps {
  name: string;
  isSelf: boolean;
  onTransfer: () => void;
  rows: AccessAssignment[];
  keySettings: AccessKeySetting[];
  areas: AccessArea[];
}

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Stack
    direction="row"
    align="center"
    justify="between"
    gap={4}
    className="w-full border-b border-line px-4 py-3 last:border-b-0"
  >
    <Text tone="muted">{label}</Text>
    <Text variant="label">{value}</Text>
  </Stack>
);

export const LockedAccessSummary = ({
  name,
  isSelf,
  onTransfer,
  rows,
  keySettings,
  areas,
}: LockedAccessSummaryProps) => {
  const full = rows.find((row) => row.full_workbook_access);

  return (
    <Stack className="w-full" align="start" gap={4}>
      <Alert color="information">
        <Stack gap={2} align="start">
          <Stack align="start" gap={1}>
            <Text variant="label">
              {isSelf ? "Your" : `${name}'s`} access stays as it is
            </Text>
            <Text>
              {isSelf
                ? "You're the only manager with whole-workbook edit, so it can't be changed from here. That keeps this page available to you."
                : "They're the only manager with whole-workbook edit, so it can't be changed from here. Someone at the business has to keep managing access."}
            </Text>
          </Stack>
          <Button variant="secondary" size="sm" onClick={onTransfer}>
            How to change manager access
          </Button>
        </Stack>
      </Alert>
      <Stack
        align="start"
        gap={0}
        className="w-full overflow-hidden rounded-xl border border-line bg-surface-raised"
      >
        {full ? (
          <>
            <SummaryRow label="Access" value="Whole workbook" />
            <SummaryRow
              label="Level"
              value={full.access_level === "edit" ? "Can edit" : "View only"}
            />
          </>
        ) : (
          <Stack align="start" gap={4} className="px-4 py-3">
            <AccessChips rows={rows} keySettings={keySettings} areas={areas} />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
